import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence, type Variants } from 'framer-motion';
import {
  ArrowLeft, Users, Copy, Check, Shuffle, /*Trophy, */Clock,
  UserPlus, UserMinus, Shield, ShieldOff, Trash2, Loader2, Settings2, Dice5, RefreshCw, Printer,
  ArrowDownToLine, ArrowUpToLine, Pencil, Swords,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppShell } from '../components/layout/AppShell';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { BracketView } from '../components/competitions/BracketView';
import { MatchResultModal } from '../components/competitions/MatchResultModal';
import { SubstitutePlayerModal } from '../components/competitions/SubstitutePlayerModal';
import { GroupLayoutModal, type GroupLayoutChoice } from '../components/competitions/GroupLayoutModal';
import { CompetitionImage } from '../components/competitions/CompetitionImage';
import { PrintableSheets } from '../components/competitions/print/PrintableSheets';
import { usePrintSheets } from '../components/competitions/print/usePrintSheets';
import { api } from '../services/api';
import { shortName, compactName } from '../utils/playerName';
import { formatSetScore } from '../utils/matchScore';
import { COMPETITION_LIMITS } from '../config/competitionLimits';
import { canonicalOrigin } from '../utils/canonicalOrigin';
import type {
  CompetitionDetail,
  CompetitionGroupsData,
  CompetitionBracketData,
  CompetitionMatch,
  CompetitionTeam,
} from '../types';

type Tab = 'players' | 'groups' | 'bracket' | 'podium';

const STATUS_LABEL: Record<CompetitionDetail['status'], string> = {
  registration: 'Inscrições abertas',
  group_stage: 'Fase de grupos',
  knockout: 'Mata-mata',
  finished: 'Encerrado',
  canceled: 'Cancelado',
};

function teamName(team: CompetitionTeam | null): string {
  if (!team) return '—';
  return team.players.map((p) => shortName(p.name) || '?').join(' & ');
}

/** Tighter name format used in the group standings/match rows: "Yago P. & Ana C.". */
function compactTeamName(team: CompetitionTeam | null): string {
  if (!team) return '—';
  return team.players.map((p) => compactName(p.name) || '?').join(' & ');
}

/**
 * Group-size layouts the admin can pick from at draw time. Only 8 duplas has a
 * genuine choice today (two groups of 4, or three groups of 3+3+2); every other
 * count has a single sensible split and draws straight away (empty list → no
 * prompt). Keep in sync with the backend's `planGroupSizeOptions`
 * (apps/backend/src/services/competitionBracket.ts), which validates the choice.
 */
function groupLayoutChoices(teamCount: number): GroupLayoutChoice[] {
  if (teamCount === 8) {
    return [
      { sizes: [4, 4], title: '2 grupos de 4', detail: 'Dois grupos com 4 duplas cada' },
      { sizes: [3, 3, 2], title: '3 grupos', detail: 'Dois grupos de 3 duplas e um de 2' },
    ];
  }
  return [];
}

// Shared variants for staggered "cards rising in" animations.
const STACK_CONTAINER: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.07, delayChildren: 0.04 } },
};
const STACK_ITEM: Variants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.28, ease: 'easeOut' } },
};

// ============================================================================
// Skeleton — shown while the detail (+ groups + bracket) is loading
// ============================================================================

function CompetitionDetailSkeleton() {
  return (
    <AppShell>
      <div className="space-y-5 max-w-2xl mx-auto animate-pulse">
        {/* Back link */}
        <div className="h-4 w-24 bg-white/10 rounded mb-3" />

        {/* Header: image + title + subtitle */}
        <div className="flex items-start gap-3">
          <div className="w-14 h-14 rounded-2xl bg-white/10 shrink-0" />
          <div className="flex-1 space-y-2 pt-1.5">
            <div className="h-5 w-40 bg-white/10 rounded" />
            <div className="h-3 w-24 bg-white/10 rounded" />
          </div>
        </div>

        {/* Tabs strip */}
        <div className="h-11 rounded-xl bg-white/5" />

        {/* Content placeholders (mimic stacked cards) */}
        <div className="space-y-3">
          <div className="h-20 rounded-2xl bg-white/5" />
          <div className="h-20 rounded-2xl bg-white/5" />
          <div className="h-20 rounded-2xl bg-white/5" />
        </div>
      </div>
    </AppShell>
  );
}

export function CompetitionDetailPage() {
  const navigate = useNavigate();
  const { competitionId } = useParams<{ competitionId: string }>();

  const [detail, setDetail] = useState<CompetitionDetail | null>(null);
  const [groups, setGroups] = useState<CompetitionGroupsData | null>(null);
  const [bracket, setBracket] = useState<CompetitionBracketData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>('players');
  // Direction of the tab-change slide (+1 = next tab, -1 = previous, 0 = initial)
  const [tabDir, setTabDir] = useState(0);
  const tabOrderRef = useRef<Tab[]>([]);

  const switchTab = (next: Tab) => {
    if (next === activeTab) return;
    const order = tabOrderRef.current;
    const prevIdx = order.indexOf(activeTab);
    const nextIdx = order.indexOf(next);
    setTabDir(prevIdx >= 0 && nextIdx >= 0 ? Math.sign(nextIdx - prevIdx) : 1);
    setActiveTab(next);
  };
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [resultMatch, setResultMatch] = useState<CompetitionMatch | null>(null);
  const [showResultModal, setShowResultModal] = useState(false);
  const [showSubModal, setShowSubModal] = useState(false);
  const [subTeamId, setSubTeamId] = useState<string | null>(null);
  const [showGroupLayoutModal, setShowGroupLayoutModal] = useState(false);
  const printSheets = usePrintSheets();

  const loadAll = useCallback(async () => {
    if (!competitionId) return;
    const res = await api.competitions.detail(competitionId);
    if (!res.success || !res.data) {
      setNotFound(true);
      setIsLoading(false);
      return;
    }
    const d = res.data;
    setDetail(d);

    // Fetch phase-specific data in parallel. Both the group standings and the
    // (pre-seeded) bracket exist from the moment teams are drawn.
    const promises: Promise<void>[] = [];
    if (d.status !== 'registration') {
      promises.push(
        api.competitions.groups(competitionId).then((r) => {
          if (r.success && r.data) setGroups(r.data);
        })
      );
      promises.push(
        api.competitions.bracket(competitionId).then((r) => {
          if (r.success && r.data) setBracket(r.data);
        })
      );
    }
    await Promise.all(promises);

    // Pick a sensible default tab on first load
    setActiveTab((prev) => {
      if (prev !== 'players') return prev;
      if (d.status === 'finished') return 'podium';
      if (d.status === 'knockout') return 'bracket';
      if (d.status === 'group_stage') return 'groups';
      return 'players';
    });

    setIsLoading(false);
  }, [competitionId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [didRefresh, setDidRefresh] = useState(false);
  const handleRefresh = useCallback(async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await loadAll();
      // Brief flash so the user sees the page refreshed (matches rankings UX).
      setDidRefresh(true);
      setTimeout(() => setDidRefresh(false), 600);
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, loadAll]);

  const runAction = async (key: string, fn: () => Promise<{ success: boolean; error?: { message: string } }>) => {
    setActionLoading(key);
    setActionError(null);
    try {
      const res = await fn();
      if (!res.success) {
        setActionError(res.error?.message || 'Erro ao executar ação');
      } else {
        await loadAll();
      }
    } catch {
      setActionError('Erro de conexão');
    } finally {
      setActionLoading(null);
    }
  };

  const handleShareInvite = async () => {
    if (!detail) return;
    const inviteUrl = `${canonicalOrigin()}/tournaments/join/${detail.inviteCode}`;
    const shareText = `Entre no torneio "${detail.name}" no BT Vision! Código: ${detail.inviteCode}`;

    if (navigator.share) {
      try {
        await navigator.share({ title: `Torneio ${detail.name}`, text: shareText, url: inviteUrl });
      } catch (err) {
        if ((err as Error).name !== 'AbortError') copyInvite();
      }
    } else {
      copyInvite();
    }
  };

  const copyInvite = () => {
    if (!detail) return;
    navigator.clipboard.writeText(`${canonicalOrigin()}/tournaments/join/${detail.inviteCode}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleEnterResult = (match: CompetitionMatch) => {
    setResultMatch(match);
    setShowResultModal(true);
  };

  const openSubstitute = (teamId: string) => {
    setSubTeamId(teamId);
    setShowSubModal(true);
  };

  if (isLoading) {
    return <CompetitionDetailSkeleton />;
  }

  if (notFound || !detail) {
    return (
      <AppShell>
        <div className="text-center py-20">
          <p className="text-white/60">Torneio não encontrado.</p>
          <Button onClick={() => navigate('/tournaments')} variant="secondary" className="mt-4">
            Voltar
          </Button>
        </div>
      </AppShell>
    );
  }

  const isAdmin = detail.myRole === 'admin';
  // Admins/creators can print blank pen-and-paper score sheets once the draw
  // exists (groups + bracket are fetched for any non-registration status).
  const canPrint = (isAdmin || detail.isCreator) && detail.status !== 'registration';
  const activePlayers = detail.players.filter((p) => p.status === 'active');
  const pendingPlayers = detail.players.filter((p) => p.status === 'pending');
  const waitlistedPlayers = detail.players.filter((p) => p.status === 'waitlisted');

  const availableTabs: { key: Tab; label: string }[] = [
    { key: 'players', label: 'Inscritos' },
  ];
  if (detail.status !== 'registration') {
    availableTabs.push({ key: 'groups', label: 'Grupos' });
    // The bracket is pre-seeded at draw time, so the Chave tab is available
    // from the group stage onward (shows placeholder slots until groups finish).
    if (bracket && bracket.rounds.length > 0) {
      availableTabs.push({ key: 'bracket', label: 'Chave' });
    }
  }
  // Pódio only after the final (and the 3rd-place playoff, if any) is decided —
  // backend only flips status to 'finished' once every knockout match is done.
  if (detail.status === 'finished') {
    availableTabs.push({ key: 'podium', label: 'Pódio' });
  }
  // Keep the order in a ref so the tab-direction calc reads the latest order.
  tabOrderRef.current = availableTabs.map((t) => t.key);

  return (
    <AppShell
      headerRightElement={
        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="p-2 text-white/70 hover:text-turquoise hover:bg-white/10 rounded-lg transition-colors disabled:opacity-50"
          aria-label="Atualizar"
        >
          <RefreshCw className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
        </button>
      }
    >
      <div className={`space-y-5 max-w-2xl mx-auto rounded-xl ${didRefresh ? 'animate-data-refresh' : ''}`}>
        {/* Header */}
        <div>
          <button
            onClick={() => navigate('/tournaments')}
            className="flex items-center gap-1.5 text-white/50 hover:text-white text-sm mb-3 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Torneios
          </button>

          <div className="flex items-start gap-3">
            <CompetitionImage
              imageUrl={detail.imageUrl}
              size="md"
              editable={isAdmin && detail.status === 'registration'}
              onImageSelected={async (base64) => {
                setActionError(null);
                const res = await api.competitions.uploadImage(detail.id, base64);
                if (res.success) {
                  await loadAll();
                } else {
                  setActionError(res.error?.message || 'Erro ao enviar imagem');
                }
              }}
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-display text-xl font-bold text-white">{detail.name}</h1>
                {isAdmin && (
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-turquoise/20 text-turquoise">
                    Admin
                  </span>
                )}
                {actionLoading && <Loader2 className="w-3.5 h-3.5 text-white/40 animate-spin" />}
              </div>
              <p className="text-sm text-white/50 mt-0.5">{STATUS_LABEL[detail.status]}</p>
              {detail.description && (
                <p className="text-sm text-white/40 mt-1 whitespace-pre-line">{detail.description}</p>
              )}
            </div>

            {canPrint && (
              <button
                onClick={printSheets}
                className="shrink-0 p-2 text-white/70 hover:text-turquoise hover:bg-white/10 rounded-lg transition-colors"
                aria-label="Imprimir fichas"
                title="Imprimir fichas"
              >
                <Printer className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Invite */}
          {detail.status === 'registration' && (
            <Card className="mt-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-white/40 uppercase tracking-wider mb-1">Código de convite</p>
                  <p className="font-mono text-lg font-bold text-turquoise tracking-widest">
                    {detail.inviteCode}
                  </p>
                </div>
                <Button onClick={handleShareInvite} size="sm" variant="secondary">
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied ? 'Copiado!' : 'Compartilhar'}
                </Button>
              </div>
            </Card>
          )}
        </div>

        {/* Phase action button (admin) */}
        {isAdmin && (
          <PhaseAction
            detail={detail}
            activePlayerCount={activePlayers.length}
            loading={actionLoading}
            onDrawTeams={() => runAction('drawTeams', () => api.competitions.drawTeams(detail.id))}
            onDrawGroups={() => {
              // When more than one layout is sensible (8 duplas), let the admin
              // choose first; otherwise draw straight away as before.
              if (groupLayoutChoices(detail.teams.length).length > 0) {
                setShowGroupLayoutModal(true);
              } else {
                runAction('drawGroups', () => api.competitions.drawGroups(detail.id));
              }
            }}
          />
        )}

        {/* During the group stage the knockout bracket already exists with
            placeholder slots; it fills in automatically as groups finish. */}
        {isAdmin && detail.status === 'group_stage' && (
          <p className="text-xs text-white/40 text-center">
            A chave já está montada na aba <span className="text-white/60">Chave</span> e será
            preenchida automaticamente conforme os grupos terminam.
          </p>
        )}

        {/* Champion banner */}
        {/* detail.status === 'finished' && bracket?.champion && (
          <div className="flex items-center justify-center gap-2 p-3 rounded-2xl bg-gradient-to-r from-sunny/20 to-coral/20 border border-sunny/30">
            <Trophy className="w-5 h-5 text-sunny" />
            <span className="text-white font-semibold">Campeão: {teamName(bracket.champion)}</span>
          </div>
        ) */}

        {actionError && <p className="text-coral text-sm">{actionError}</p>}

        {/* Tabs */}
        <div className="flex gap-2 p-1 rounded-xl bg-white/5">
          {availableTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => switchTab(tab.key)}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === tab.key ? 'bg-turquoise/20 text-turquoise' : 'text-white/50 hover:text-white'
                }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content with directional slide transition */}
        <div className="relative overflow-hidden">
          <AnimatePresence mode="wait" custom={tabDir} initial={false}>
            <motion.div
              key={activeTab}
              custom={tabDir}
              initial={{ opacity: 0, x: tabDir >= 0 ? 30 : -30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: tabDir >= 0 ? -30 : 30 }}
              transition={{ duration: 0.22, ease: 'easeOut' }}
            >
              {activeTab === 'players' && (
                <PlayersPanel
                  detail={detail}
                  pendingPlayers={pendingPlayers}
                  activePlayers={activePlayers}
                  waitlistedPlayers={waitlistedPlayers}
                  actionLoading={actionLoading}
                  onApprove={(pid) => runAction(`approve-${pid}`, () => api.competitions.approvePlayer(detail.id, pid))}
                  onReject={(pid) => runAction(`reject-${pid}`, () => api.competitions.rejectPlayer(detail.id, pid))}
                  onRemove={(pid) => runAction(`remove-${pid}`, () => api.competitions.removePlayer(detail.id, pid))}
                  onPromote={(pid) => runAction(`promote-${pid}`, () => api.competitions.promotePlayer(detail.id, pid))}
                  onDemote={(pid) => runAction(`demote-${pid}`, () => api.competitions.demotePlayer(detail.id, pid))}
                  onUpdateMax={(max) =>
                    runAction('max', () => api.competitions.update(detail.id, { maxPlayers: max }))
                  }
                  onSetPlaying={(playing) =>
                    runAction('playing', () => api.competitions.setPlaying(detail.id, playing))
                  }
                  onQueue={(pid) => runAction(`queue-${pid}`, () => api.competitions.queuePlayer(detail.id, pid))}
                  onUnqueue={(pid) => runAction(`unqueue-${pid}`, () => api.competitions.unqueuePlayer(detail.id, pid))}
                  onSetPool={(pid, pool) =>
                    runAction(`pool-${pid}`, () => api.competitions.setPlayerPool(detail.id, pid, pool))
                  }
                  onSubstituteTeam={openSubstitute}
                  onDelete={() =>
                    runAction('delete', async () => {
                      const res = await api.competitions.delete(detail.id);
                      if (res.success) navigate('/tournaments');
                      return res;
                    })
                  }
                />
              )}

              {activeTab === 'groups' && (
                <GroupsPanel groups={groups} detail={detail} onEnterResult={handleEnterResult} />
              )}

              {activeTab === 'bracket' && bracket && (
                <BracketView data={bracket} isAdmin={isAdmin} onEnterResult={handleEnterResult} />
              )}

              {activeTab === 'podium' && <PodiumPanel detail={detail} />}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      <MatchResultModal
        match={resultMatch}
        isOpen={showResultModal}
        onClose={() => setShowResultModal(false)}
        onSubmitScore={async (matchId, data) => {
          const res = await api.competitions.enterResult(detail.id, matchId, data);
          if (!res.success) throw new Error(res.error?.message || 'Erro ao salvar');
          await loadAll();
        }}
        onSubmitWO={async (matchId, winnerTeamId) => {
          const res = await api.competitions.wo(detail.id, matchId, { winnerTeamId });
          if (!res.success) throw new Error(res.error?.message || 'Erro ao salvar');
          await loadAll();
        }}
      />

      <SubstitutePlayerModal
        detail={detail}
        isOpen={showSubModal}
        onClose={() => setShowSubModal(false)}
        teamId={subTeamId}
        onSubmit={async (teamId, outPlayerId, inPlayerId) => {
          const res = await api.competitions.replacePlayer(detail.id, teamId, {
            outPlayerId,
            inPlayerId,
          });
          if (!res.success) throw new Error(res.error?.message || 'Erro ao substituir');
          await loadAll();
        }}
        onRemove={async (teamId) => {
          const res = await api.competitions.removeTeam(detail.id, teamId);
          if (!res.success) throw new Error(res.error?.message || 'Erro ao remover dupla');
          await loadAll();
        }}
      />

      <GroupLayoutModal
        isOpen={showGroupLayoutModal}
        onClose={() => setShowGroupLayoutModal(false)}
        isLoading={actionLoading === 'drawGroups'}
        choices={groupLayoutChoices(detail.teams.length)}
        onConfirm={(sizes) => {
          setShowGroupLayoutModal(false);
          runAction('drawGroups', () => api.competitions.drawGroups(detail.id, sizes));
        }}
      />

      {canPrint && <PrintableSheets detail={detail} groups={groups} bracket={bracket} />}
    </AppShell>
  );
}

// ============================================================================
// Phase action button
// ============================================================================

function PhaseAction({
  detail,
  activePlayerCount,
  loading,
  onDrawTeams,
  onDrawGroups,
}: {
  detail: CompetitionDetail;
  activePlayerCount: number;
  loading: string | null;
  onDrawTeams: () => void;
  onDrawGroups: () => void;
}) {
  if (detail.status !== 'registration') return null;

  const hasTeams = detail.teams.length > 0;
  const minPlayers = COMPETITION_LIMITS.MIN_PLAYERS_TO_DRAW;

  // No teams yet → single primary "Sortear duplas" button.
  if (!hasTeams) {
    return (
      <Button
        onClick={onDrawTeams}
        fullWidth
        isLoading={loading === 'drawTeams'}
        disabled={activePlayerCount < minPlayers}
      >
        <Shuffle className="w-4 h-4" />
        Sortear duplas
      </Button>
    );
  }

  // Teams exist → admin can re-shuffle or move on to groups + bracket.
  return (
    <div className="space-y-2">
      <Button
        onClick={onDrawGroups}
        fullWidth
        isLoading={loading === 'drawGroups'}
      >
        <Shuffle className="w-4 h-4" />
        Sortear grupos
      </Button>
      <Button
        onClick={onDrawTeams}
        fullWidth
        variant="secondary"
        isLoading={loading === 'drawTeams'}
        disabled={activePlayerCount < minPlayers}
      >
        <Shuffle className="w-4 h-4" />
        Re-sortear duplas
      </Button>
      <p className="text-[11px] text-white/40 text-center">
        Ao sortear os grupos, as inscrições serão encerradas.
      </p>
    </div>
  );
}

// ============================================================================
// Players panel
// ============================================================================

function PlayersPanel({
  detail,
  pendingPlayers,
  activePlayers,
  waitlistedPlayers,
  actionLoading,
  onApprove,
  onReject,
  onRemove,
  onPromote,
  onDemote,
  onUpdateMax,
  onSetPlaying,
  onQueue,
  onUnqueue,
  onSetPool,
  onSubstituteTeam,
  onDelete,
}: {
  detail: CompetitionDetail;
  pendingPlayers: CompetitionDetail['players'];
  activePlayers: CompetitionDetail['players'];
  waitlistedPlayers: CompetitionDetail['players'];
  actionLoading: string | null;
  onApprove: (pid: string) => void;
  onReject: (pid: string) => void;
  onRemove: (pid: string) => void;
  onPromote: (pid: string) => void;
  onDemote: (pid: string) => void;
  onUpdateMax: (max: number | null) => void;
  onSetPlaying: (playing: boolean) => void;
  onQueue: (pid: string) => void;
  onUnqueue: (pid: string) => void;
  onSetPool: (pid: string, pool: 'A' | 'B' | 'C') => void;
  onSubstituteTeam: (teamId: string) => void;
  onDelete: () => void;
}) {
  const isAdmin = detail.myRole === 'admin';
  const isCreator = detail.isCreator;
  const canManage = isAdmin && detail.status === 'registration';
  // After the draw, the queue is a substitute pool managed via substitution.
  const postDraw = detail.status === 'group_stage' || detail.status === 'knockout';
  // Substitution is available as soon as the doubles are drawn (even while still
  // in registration), until the tournament ends. The swap keeps the active count
  // balanced, so a later "Sortear grupos" still works.
  const canSubstitute =
    isAdmin &&
    detail.teams.length > 0 &&
    detail.status !== 'finished' &&
    detail.status !== 'canceled';
  // Manual active↔queue arrows only before the doubles are drawn — afterwards
  // they would desync the drawn teams, so substitution takes over.
  const canQueueMove = canManage && detail.teams.length === 0;

  const [subTab, setSubTab] = useState<'jogadores' | 'duplas'>('jogadores');
  // Non-admins only discover their dupla once the groups are drawn (status
  // leaves registration). Until then the doubles are an admin-only view, so the
  // "Duplas" subtab — and its bar — stays hidden for regular players.
  const canSeeDoubles = isAdmin || detail.status !== 'registration';
  const activeSubTab = canSeeDoubles ? subTab : 'jogadores';

  return (
    <div className="space-y-4">
      {/* Subtab bar: Jogadores | Duplas (hidden when only Jogadores is shown) */}
      {canSeeDoubles && (
        <div className="flex gap-2 p-1 rounded-xl bg-white/5">
          {(['jogadores', 'duplas'] as const).map((k) => (
            <button
              key={k}
              onClick={() => setSubTab(k)}
              className={`flex-1 py-1.5 rounded-lg text-sm font-medium transition-colors ${activeSubTab === k ? 'bg-turquoise/15 text-turquoise' : 'text-white/50 hover:text-white'
                }`}
            >
              {k === 'jogadores' ? 'Jogadores' : 'Duplas'}
            </button>
          ))}
        </div>
      )}

      <AnimatePresence mode="wait" initial={false}>
        {activeSubTab === 'duplas' && (
          <motion.div
            key="duplas"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, transition: { duration: 0.15 } }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
          >
            <DuplasContent
              detail={detail}
              canSubstitute={canSubstitute}
              onSubstitute={onSubstituteTeam}
            />
          </motion.div>
        )}
        {activeSubTab === 'jogadores' && (
          <motion.div key="jogadores" className="space-y-4" variants={STACK_CONTAINER} initial="hidden" animate="visible" exit={{ opacity: 0, transition: { duration: 0.15 } }}>
            {/* Participation toggle — any admin (creator or delegated),
                registration only — decides whether they themselves play. */}
            {isAdmin && detail.status === 'registration' && (
              <motion.div variants={STACK_ITEM}>
                <CreatorPlayToggle
                  detail={detail}
                  loading={actionLoading === 'playing'}
                  onSetPlaying={onSetPlaying}
                />
              </motion.div>
            )}

            {/* Capacity control (admin, registration only) */}
            {canManage && (
              <motion.div variants={STACK_ITEM}>
                <CapacityControl detail={detail} loading={actionLoading === 'max'} onUpdateMax={onUpdateMax} />
              </motion.div>
            )}

            {/* Pending approvals */}
            {isAdmin && pendingPlayers.length > 0 && (
              <motion.div variants={STACK_ITEM} className="glass-card rounded-2xl p-4">
                <h3 className="text-sm font-semibold text-sunny mb-3">
                  Aguardando aprovação ({pendingPlayers.length})
                </h3>
                <div className="space-y-2">
                  {pendingPlayers.map((p) => (
                    <div key={p.id} className="flex items-center gap-3">
                      <Avatar name={p.name} url={p.avatarUrl} />
                      <span className="flex-1 text-sm text-white truncate">{p.name || 'Jogador'}</span>
                      <button
                        onClick={() => onApprove(p.id)}
                        disabled={!!actionLoading}
                        className="p-2 rounded-lg bg-turquoise/20 text-turquoise hover:bg-turquoise/30 transition-colors disabled:opacity-50"
                        aria-label="Aprovar"
                      >
                        {actionLoading === `approve-${p.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => onReject(p.id)}
                        disabled={!!actionLoading}
                        className="p-2 rounded-lg bg-coral/20 text-coral hover:bg-coral/30 transition-colors disabled:opacity-50"
                        aria-label="Rejeitar"
                      >
                        <UserMinus className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Active players — admins managing a pooled draw see one card per
                list (A/B/C); everyone else sees a single, undivided list (the
                list split is an admin-only management tool). */}
            {detail.poolCount && isAdmin ? (
              <PooledLists
                detail={detail}
                activePlayers={activePlayers}
                actionLoading={actionLoading}
                isCreator={isCreator}
                canManage={canManage}
                canQueueMove={canQueueMove}
                onPromote={onPromote}
                onDemote={onDemote}
                onQueue={onQueue}
                onRemove={onRemove}
                onSetPool={onSetPool}
              />
            ) : (
              <motion.div variants={STACK_ITEM} className="glass-card rounded-2xl p-4">
                <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4 text-turquoise" />
                  Jogadores ({activePlayers.length})
                </h3>
                {activePlayers.length === 0 ? (
                  <p className="text-sm text-white/40 py-2">Nenhum jogador inscrito ainda.</p>
                ) : (
                  <div className="space-y-2">
                    {activePlayers.map((p) => (
                      <ActivePlayerRow
                        key={p.id}
                        p={p}
                        detail={detail}
                        actionLoading={actionLoading}
                        isCreator={isCreator}
                        canManage={canManage}
                        canQueueMove={canQueueMove}
                        onPromote={onPromote}
                        onDemote={onDemote}
                        onQueue={onQueue}
                        onRemove={onRemove}
                        onSetPool={onSetPool}
                      />
                    ))}
                  </div>
                )}
              </motion.div>
            )}

            {/* Waitlist */}
            {waitlistedPlayers.length > 0 && (
              <motion.div variants={STACK_ITEM} className="glass-card rounded-2xl p-4">
                <h3 className="text-sm font-semibold text-white/70 mb-1 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-white/50" />
                  Fila de espera ({waitlistedPlayers.length})
                </h3>
                <p className="text-[11px] text-white/40 mb-3">
                  {!isAdmin
                    ? 'Jogadores aguardando uma vaga no torneio.'
                    : postDraw
                      ? 'Jogadores disponíveis para substituir alguém em uma dupla.'
                      : 'Jogadores fora do sorteio. Use a seta para trazê-los de volta aos ativos.'}
                </p>
                <div className="space-y-2">
                  {waitlistedPlayers.map((p, i) => (
                    <div key={p.id} className="flex items-center gap-3">
                      <span className="w-5 text-center text-xs text-white/30">{i + 1}</span>
                      <Avatar name={p.name} url={p.avatarUrl} />
                      <span className="flex-1 text-sm text-white/70 truncate">{p.name || 'Jogador'}</span>
                      {/* Before the draw: pull back to active. */}
                      {canQueueMove && (
                        <button
                          onClick={() => onUnqueue(p.id)}
                          disabled={!!actionLoading}
                          className="p-1.5 rounded-lg text-white/40 hover:text-turquoise hover:bg-white/10 transition-colors disabled:opacity-50"
                          aria-label="Trazer para ativos"
                          title="Trazer para ativos"
                        >
                          {actionLoading === `unqueue-${p.id}` ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <ArrowUpToLine className="w-4 h-4" />
                          )}
                        </button>
                      )}
                      {canManage && (
                        <button
                          onClick={() => onRemove(p.id)}
                          disabled={!!actionLoading}
                          className="p-1.5 rounded-lg text-white/40 hover:text-coral hover:bg-white/10 transition-colors disabled:opacity-50"
                          aria-label="Remover jogador"
                        >
                          <UserMinus className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Danger zone (creator) */}
            {isCreator && (
              <motion.button
                variants={STACK_ITEM}
                onClick={() => {
                  if (window.confirm(detail.status === 'registration' ? 'Excluir este torneio?' : 'Cancelar este torneio?')) {
                    onDelete();
                  }
                }}
                disabled={!!actionLoading}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-coral hover:bg-coral/10 transition-colors text-sm disabled:opacity-50"
              >
                {actionLoading === 'delete' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {detail.status === 'registration' ? 'Excluir torneio' : 'Cancelar torneio'}
              </motion.button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ============================================================================
// Duplas subtab — empty state until "Sortear duplas" runs, then the team list.
// ============================================================================

function DuplasContent({
  detail,
  canSubstitute,
  onSubstitute,
}: {
  detail: CompetitionDetail;
  canSubstitute: boolean;
  onSubstitute: (teamId: string) => void;
}) {
  // Admins see each player's list letter (A/B/C) on a pooled draw, to spot and
  // fix unbalanced duplas via substitution/swap.
  const showPools = detail.myRole === 'admin' && !!detail.poolCount;
  const label = (team: CompetitionTeam) =>
    team.players
      .map((p) => {
        const n = shortName(p.name) || '?';
        return showPools && p.pool ? `${n} (${p.pool})` : n;
      })
      .join(' & ');

  if (detail.teams.length === 0) {
    return (
      <div className="glass-card rounded-2xl p-6 text-center">
        <Shuffle className="w-7 h-7 text-white/30 mx-auto mb-3" />
        <p className="text-sm text-white/70">Sorteio das duplas pendente</p>
        <p className="text-[11px] text-white/40 mt-1 max-w-xs mx-auto">
          Use “Sortear duplas” acima para gerar as duplas a partir dos jogadores inscritos.
        </p>
      </div>
    );
  }

  return (
    <div className="glass-card rounded-2xl p-4">
      <h3 className="text-sm font-semibold text-white mb-3">
        Duplas ({detail.teams.length})
      </h3>
      <div className="space-y-2">
        {detail.teams.map((team, idx) => (
          <motion.div
            key={team.id}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.04, duration: 0.22, ease: 'easeOut' }}
            className="flex items-center gap-3 px-3 py-2 rounded-xl bg-white/5"
          >
            <span className="w-6 text-center text-sm font-semibold text-turquoise">
              {team.teamNumber}
            </span>
            <span className="flex-1 text-sm text-white truncate">{label(team)}</span>
            {canSubstitute && (
              <button
                onClick={() => onSubstitute(team.id)}
                className="shrink-0 p-1.5 rounded-lg text-white/40 hover:text-turquoise hover:bg-white/10 transition-colors"
                aria-label="Substituir jogador"
                title="Substituir jogador"
              >
                <Pencil className="w-4 h-4" />
              </button>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function Avatar({ name, url }: { name: string | null; url: string | null }) {
  return url ? (
    <img src={url} alt={name || ''} className="w-9 h-9 rounded-full object-cover shrink-0" />
  ) : (
    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-turquoise to-coral flex items-center justify-center text-white font-bold text-sm shrink-0">
      {name?.charAt(0) || '?'}
    </div>
  );
}

// ============================================================================
// Pooled-draw list assignment (admin, registration, before the draw)
// ============================================================================

const POOL_LETTERS = ['A', 'B', 'C'] as const;

/** Compact A | B [| C] selector to move a player between lists. */
function PoolSelector({
  value,
  count,
  disabled,
  onChange,
}: {
  value: 'A' | 'B' | 'C' | null;
  count: number;
  disabled: boolean;
  onChange: (pool: 'A' | 'B' | 'C') => void;
}) {
  const letters = POOL_LETTERS.slice(0, count === 3 ? 3 : 2);
  return (
    <div className="flex gap-0.5 shrink-0">
      {letters.map((l) => {
        const active = value === l;
        return (
          <button
            key={l}
            type="button"
            disabled={disabled}
            onClick={() => !active && onChange(l)}
            className={`w-6 h-6 rounded-md text-[11px] font-bold transition-colors disabled:opacity-50 ${
              active ? 'bg-turquoise/30 text-turquoise' : 'text-white/40 hover:text-white hover:bg-white/10'
            }`}
            aria-label={`Lista ${l}`}
          >
            {l}
          </button>
        );
      })}
    </div>
  );
}

type ActivePlayerRowProps = {
  p: CompetitionDetail['players'][number];
  detail: CompetitionDetail;
  actionLoading: string | null;
  isCreator: boolean;
  canManage: boolean;
  canQueueMove: boolean;
  onPromote: (pid: string) => void;
  onDemote: (pid: string) => void;
  onQueue: (pid: string) => void;
  onRemove: (pid: string) => void;
  onSetPool: (pid: string, pool: 'A' | 'B' | 'C') => void;
};

/** One active-player row: name + admin/queue/remove controls and, in a pooled
 *  draw, the list selector (admin, before the draw). */
function ActivePlayerRow({
  p,
  detail,
  actionLoading,
  isCreator,
  canManage,
  canQueueMove,
  onPromote,
  onDemote,
  onQueue,
  onRemove,
  onSetPool,
}: ActivePlayerRowProps) {
  const isSelf = p.id === detail.myPlayerId;
  const showPool = !!detail.poolCount && canManage && detail.teams.length === 0;
  return (
    <div className="flex items-center gap-2">
      <Avatar name={p.name} url={p.avatarUrl} />
      <div className="flex-1 min-w-0">
        <span className="text-sm text-white truncate flex items-center gap-1.5">
          {p.name || 'Jogador'}
          {p.role === 'admin' && <Shield className="w-3.5 h-3.5 text-turquoise" />}
        </span>
      </div>
      {showPool && (
        <PoolSelector
          value={p.pool}
          count={detail.poolCount!}
          disabled={!!actionLoading}
          onChange={(pool) => onSetPool(p.id, pool)}
        />
      )}
      {/* Creator: promote players, demote other admins */}
      {isCreator && p.role === 'player' && (
        <button
          onClick={() => onPromote(p.id)}
          disabled={!!actionLoading}
          className="p-1.5 rounded-lg text-white/40 hover:text-turquoise hover:bg-white/10 transition-colors disabled:opacity-50"
          aria-label="Promover a admin"
        >
          <Shield className="w-4 h-4" />
        </button>
      )}
      {isCreator && p.role === 'admin' && !isSelf && (
        <button
          onClick={() => onDemote(p.id)}
          disabled={!!actionLoading}
          className="p-1.5 rounded-lg text-white/40 hover:text-coral hover:bg-white/10 transition-colors disabled:opacity-50"
          aria-label="Rebaixar admin"
        >
          <ShieldOff className="w-4 h-4" />
        </button>
      )}
      {/* Admin: send to queue (before the draw); never the creator */}
      {canQueueMove && !isSelf && (
        <button
          onClick={() => onQueue(p.id)}
          disabled={!!actionLoading}
          className="p-1.5 rounded-lg text-white/40 hover:text-sunny hover:bg-white/10 transition-colors disabled:opacity-50"
          aria-label="Enviar para a fila"
          title="Enviar para a fila"
        >
          {actionLoading === `queue-${p.id}` ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <ArrowDownToLine className="w-4 h-4" />
          )}
        </button>
      )}
      {/* Admin: remove (registration only); never the creator */}
      {canManage && !isSelf && (
        <button
          onClick={() => onRemove(p.id)}
          disabled={!!actionLoading}
          className="p-1.5 rounded-lg text-white/40 hover:text-coral hover:bg-white/10 transition-colors disabled:opacity-50"
          aria-label="Remover jogador"
        >
          <UserMinus className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

/** Active players split into one card per list (Lista A/B[/C]) for a pooled
 *  draw, plus a "Sem lista" card for any not-yet-assigned players. */
function PooledLists({
  detail,
  activePlayers,
  actionLoading,
  isCreator,
  canManage,
  canQueueMove,
  onPromote,
  onDemote,
  onQueue,
  onRemove,
  onSetPool,
}: {
  detail: CompetitionDetail;
  activePlayers: CompetitionDetail['players'];
  actionLoading: string | null;
  isCreator: boolean;
  canManage: boolean;
  canQueueMove: boolean;
  onPromote: (pid: string) => void;
  onDemote: (pid: string) => void;
  onQueue: (pid: string) => void;
  onRemove: (pid: string) => void;
  onSetPool: (pid: string, pool: 'A' | 'B' | 'C') => void;
}) {
  const count = detail.poolCount ?? 2;
  const letters = POOL_LETTERS.slice(0, count === 3 ? 3 : 2);
  const valid = new Set<string>(letters);
  const unassigned = activePlayers.filter((p) => !p.pool || !valid.has(p.pool));

  const rowProps = {
    detail,
    actionLoading,
    isCreator,
    canManage,
    canQueueMove,
    onPromote,
    onDemote,
    onQueue,
    onRemove,
    onSetPool,
  };

  return (
    <>
      {letters.map((l) => {
        const members = activePlayers.filter((p) => p.pool === l);
        return (
          <motion.div key={l} variants={STACK_ITEM} className="glass-card rounded-2xl p-4">
            <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
              <Users className="w-4 h-4 text-turquoise" />
              Lista {l} ({members.length})
            </h3>
            {members.length === 0 ? (
              <p className="text-sm text-white/40 py-1">Nenhum jogador nesta lista.</p>
            ) : (
              <div className="space-y-2">
                {members.map((p) => (
                  <ActivePlayerRow key={p.id} p={p} {...rowProps} />
                ))}
              </div>
            )}
          </motion.div>
        );
      })}

      {unassigned.length > 0 && (
        <motion.div variants={STACK_ITEM} className="glass-card rounded-2xl p-4">
          <h3 className="text-sm font-semibold text-white/70 mb-3 flex items-center gap-2">
            <Users className="w-4 h-4 text-white/50" />
            Sem lista ({unassigned.length})
          </h3>
          <div className="space-y-2">
            {unassigned.map((p) => (
              <ActivePlayerRow key={p.id} p={p} {...rowProps} />
            ))}
          </div>
        </motion.div>
      )}
    </>
  );
}

// ============================================================================
// Creator participation toggle (creator, registration only)
// ============================================================================

function CreatorPlayToggle({
  detail,
  loading,
  onSetPlaying,
}: {
  detail: CompetitionDetail;
  loading: boolean;
  onSetPlaying: (playing: boolean) => void;
}) {
  const playing = detail.myStatus !== 'organizer';
  const waitlisted = detail.myStatus === 'waitlisted';

  return (
    <div className="glass-card rounded-2xl p-4">
      <button
        type="button"
        onClick={() => !loading && onSetPlaying(!playing)}
        disabled={loading}
        className="w-full flex items-center justify-between gap-3 text-left disabled:opacity-60"
      >
        <span className="text-sm font-semibold text-white flex items-center gap-2">
          {loading ? <Loader2 className="w-4 h-4 text-turquoise animate-spin" /> : <UserPlus className="w-4 h-4 text-turquoise" />}
          Vou jogar neste torneio
        </span>
        <span
          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
            playing ? 'bg-turquoise' : 'bg-white/15'
          }`}
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
              playing ? 'translate-x-5' : 'translate-x-0.5'
            }`}
          />
        </span>
      </button>
      <p className="text-[11px] text-white/40 mt-2 leading-snug">
        {playing
          ? waitlisted
            ? 'Você entrou na fila de espera. Entre em uma dupla por uma substituição.'
            : 'Você entra no sorteio das duplas como participante.'
          : 'Você apenas organiza — não entra no sorteio das duplas.'}
      </p>
    </div>
  );
}

// ============================================================================
// Capacity control (admin, registration only)
// ============================================================================

function CapacityControl({
  detail,
  loading,
  onUpdateMax,
}: {
  detail: CompetitionDetail;
  loading: boolean;
  onUpdateMax: (max: number | null) => void;
}) {
  const [hasLimit, setHasLimit] = useState(detail.maxPlayers != null);
  const [value, setValue] = useState(detail.maxPlayers ?? 16);

  const min = COMPETITION_LIMITS.MIN_PLAYERS_TO_DRAW;
  const max = COMPETITION_LIMITS.MAX_PLAYERS_PER_COMPETITION;
  const target = hasLimit ? value : null;
  const dirty = target !== detail.maxPlayers;

  return (
    <div className="glass-card rounded-2xl p-4">
      <h3 className="text-sm font-semibold text-white mb-1 flex items-center gap-2">
        <Settings2 className="w-4 h-4 text-turquoise" />
        Vagas
      </h3>
      <p className="text-[11px] text-white/40 mb-3">
        Com limite, quem passar do número vai para a fila. A entrada e saída da fila são manuais
        (use as setas na lista de jogadores).
      </p>

      <button
        type="button"
        onClick={() => setHasLimit((v) => !v)}
        className="w-full flex items-center justify-between gap-3 text-left mb-2"
      >
        <span className="text-sm text-white/70">Limitar número de jogadores</span>
        <span
          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${hasLimit ? 'bg-turquoise' : 'bg-white/15'
            }`}
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${hasLimit ? 'translate-x-5' : 'translate-x-0.5'
              }`}
          />
        </span>
      </button>

      {hasLimit && (
        <div className="flex items-center justify-center gap-3 my-3">
          <button
            onClick={() => setValue((v) => Math.max(min, v - 1))}
            className="w-8 h-8 rounded-lg bg-white/10 text-white/70 hover:bg-white/20 flex items-center justify-center"
          >
            −
          </button>
          <span className="w-12 text-center text-2xl font-bold text-white">{value}</span>
          <button
            onClick={() => setValue((v) => Math.min(max, v + 1))}
            className="w-8 h-8 rounded-lg bg-white/10 text-white/70 hover:bg-white/20 flex items-center justify-center"
          >
            +
          </button>
        </div>
      )}

      {dirty && (
        <Button onClick={() => onUpdateMax(target)} size="sm" fullWidth isLoading={loading}>
          {hasLimit ? `Definir limite de ${value}` : 'Remover limite'}
        </Button>
      )}
    </div>
  );
}

// ============================================================================
// Groups panel
// ============================================================================

function GroupsPanel({
  groups,
  detail,
  onEnterResult,
}: {
  groups: CompetitionGroupsData | null;
  detail: CompetitionDetail;
  onEnterResult: (match: CompetitionMatch) => void;
}) {
  if (!groups || groups.groups.length === 0) {
    return <p className="text-center text-white/40 text-sm py-8">As duplas ainda não foram sorteadas.</p>;
  }

  const isAdmin = groups.isAdmin;
  // Group results stay editable through the group stage, and into the knockout
  // until the first bracket match is played (the backend rebuilds the bracket on
  // any late edit). Once a knockout match has a result, they're frozen.
  const canEnter = isAdmin && (groups.canEditGroupResults ?? detail.status === 'group_stage');
  // Editing here will rebuild the already-drawn bracket.
  const editRebuildsBracket = canEnter && detail.status === 'knockout';
  const advancing = detail.teamsAdvancingPerGroup;

  return (
    <div className="space-y-5">
      {editRebuildsBracket && (
        <div className="flex items-start gap-2 p-2.5 rounded-xl bg-sunny/10 text-sunny/90">
          <Shuffle className="w-4 h-4 shrink-0 mt-0.5" />
          <p className="text-[11px] leading-snug">
            O mata-mata já foi sorteado. Editar um resultado da fase de grupos vai refazer a chave
            com a nova classificação. Isso só é possível enquanto nenhum jogo do mata-mata tiver
            resultado.
          </p>
        </div>
      )}
      {groups.groups.map((group) => {
        const tiedIds = new Set(group.randomTiebreakTeamIds ?? []);
        const h2hIds = new Set(group.headToHeadTiebreakTeamIds ?? []);
        return (
          <div key={group.id} className="glass-card rounded-2xl p-4">
            <h3 className="font-semibold text-white mb-3">Grupo {group.groupNumber}</h3>

            {/* Standings */}
            <div className="mb-3">
              <div className="grid grid-cols-[1.5rem_1fr_2rem_2rem_2.5rem] gap-2 text-[11px] text-white/40 uppercase tracking-wide px-1 mb-1">
                <span>#</span>
                <span>Dupla</span>
                <span className="text-center">V</span>
                <span className="text-center">D</span>
                <span className="text-center">Saldo</span>
              </div>
              {group.teams.map((team) => {
                const qualifies = team.groupRank != null && team.groupRank <= advancing;
                return (
                  <div
                    key={team.id}
                    className={`grid grid-cols-[1.5rem_1fr_2rem_2rem_2.5rem] gap-2 items-center px-1 py-1.5 rounded-lg ${qualifies ? 'bg-turquoise/10' : ''
                      }`}
                  >
                    <span className={`text-sm font-semibold ${qualifies ? 'text-turquoise' : 'text-white/50'} pl-2`}>
                      {team.groupRank ?? '-'}
                    </span>
                    <span className="text-sm text-white truncate flex items-center gap-1">
                      {compactTeamName(team)}
                    </span>
                    <span className="text-sm text-white/70 text-center">{team.wins}</span>
                    <span className="text-sm text-white/70 text-center">{team.losses}</span>
                    <span className="text-sm text-white/70 text-center">
                      {team.gamesWon - team.gamesLost > 0 ? '+' : ''}
                      {team.gamesWon - team.gamesLost}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Head-to-head tiebreak notice */}
            {h2hIds.size > 0 && (
              <div className="flex items-start gap-2 mb-4 p-2.5 rounded-xl bg-turquoise/10 text-turquoise/90">
                <Swords className="w-4 h-4 shrink-0 mt-0.5" />
                <p className="text-[11px] leading-snug">
                  Empate em vitórias e saldo de games. A classificação foi definida pelo confronto
                  direto (a dupla que venceu o jogo entre elas fica à frente).
                </p>
              </div>
            )}

            {/* Dead-heat notice */}
            {tiedIds.size > 0 && (
              <div className="flex items-start gap-2 mb-4 p-2.5 rounded-xl bg-sunny/10 text-sunny/90">
                <Dice5 className="w-4 h-4 shrink-0 mt-0.5" />
                <p className="text-[11px] leading-snug">
                  Empate total entre as {tiedIds.size} duplas (quatidade de vitórias e saldo de games).
                  A ordem foi definida por sorteio.
                </p>
              </div>
            )}

            {/* Matches */}
            <div className="space-y-2">
              {group.matches.map((match) => (
                <GroupMatchRow
                  key={match.id}
                  match={match}
                  canEnter={canEnter}
                  onEnterResult={onEnterResult}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function GroupMatchRow({
  match,
  canEnter,
  onEnterResult,
}: {
  match: CompetitionMatch;
  canEnter: boolean;
  onEnterResult: (match: CompetitionMatch) => void;
}) {
  const done = match.status === 'completed';
  const t1Win = match.winnerTeamId === match.team1?.id;
  const t2Win = match.winnerTeamId === match.team2?.id;

  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/5">
      <div className="flex-1 min-w-0 space-y-0.5">
        <div className="flex items-center justify-between gap-2">
          <span className={`text-sm truncate ${t1Win ? 'text-white font-semibold' : 'text-white/60'}`}>
            {compactTeamName(match.team1)}
          </span>
          <span className={`text-sm shrink-0 ${t1Win ? 'text-turquoise font-bold' : 'text-white/40'}`}>
            {match.isWo
              ? t1Win ? 'W.O.' : '—'
              : done ? formatSetScore(match.team1Games, match.team2Games, match.tiebreakScore) : ''}
          </span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className={`text-sm truncate ${t2Win ? 'text-white font-semibold' : 'text-white/60'}`}>
            {compactTeamName(match.team2)}
          </span>
          <span className={`text-sm shrink-0 ${t2Win ? 'text-turquoise font-bold' : 'text-white/40'}`}>
            {match.isWo
              ? t2Win ? 'W.O.' : '—'
              : done ? formatSetScore(match.team2Games, match.team1Games, match.tiebreakScore) : ''}
          </span>
        </div>
      </div>
      {canEnter && (
        <button
          onClick={() => onEnterResult(match)}
          className="shrink-0 px-2.5 py-1.5 rounded-lg bg-turquoise/20 text-turquoise text-xs font-medium hover:bg-turquoise/30 transition-colors"
        >
          {done ? 'Editar' : 'Resultado'}
        </button>
      )}
    </div>
  );
}

// ============================================================================
// Podium (shown when the competition is finished)
// ============================================================================

function PodiumPanel({ detail }: { detail: CompetitionDetail }) {
  const first = detail.teams.find((t) => t.finalPosition === 1) ?? null;
  const second = detail.teams.find((t) => t.finalPosition === 2) ?? null;
  const third = detail.teams.find((t) => t.finalPosition === 3) ?? null;

  if (!first) {
    return (
      <p className="text-center text-white/40 text-sm py-8">
        O pódio ainda não foi definido.
      </p>
    );
  }

  // Variant-driven stagger (not per-element delays): orchestration propagates
  // through context, so it survives re-renders that otherwise dropped the
  // delayed tweens on mobile (medals would snap in without animating).
  return (
    <motion.div className="space-y-3" variants={PODIUM_CONTAINER} initial="hidden" animate="visible">
      <PodiumRow position={1} team={first} />
      {second && <PodiumRow position={2} team={second} />}
      {/* 3rd may be absent for very small brackets (Q=2, no semifinals). */}
      {third && <PodiumRow position={3} team={third} />}
    </motion.div>
  );
}

const PODIUM_META = {
  1: { medal: '🥇', label: '1º lugar — Campeão', accent: 'border-sunny/50 bg-gradient-to-r from-sunny/15 to-coral/10' },
  2: { medal: '🥈', label: '2º lugar — Vice', accent: 'border-white/20 bg-white/[0.04]' },
  3: { medal: '🥉', label: '3º lugar', accent: 'border-coral/20 bg-coral/[0.06]' },
} as const;

const PODIUM_CONTAINER: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15, delayChildren: 0.05 } },
};
const PODIUM_ROW: Variants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: 'easeOut' } },
};
const PODIUM_MEDAL: Variants = {
  hidden: { scale: 0.6, opacity: 0 },
  visible: { scale: 1, opacity: 1, transition: { duration: 0.28, ease: 'easeOut', delay: 0.06 } },
};

function PodiumRow({ position, team }: { position: 1 | 2 | 3; team: CompetitionTeam }) {
  const meta = PODIUM_META[position];
  // Each row fades up and its medal pops; the parent container staggers the rows.
  return (
    <motion.div
      variants={PODIUM_ROW}
      className={`flex items-center gap-4 p-4 rounded-2xl border ${meta.accent}`}
    >
      <motion.span className="text-4xl shrink-0" aria-hidden variants={PODIUM_MEDAL}>
        {meta.medal}
      </motion.span>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] uppercase tracking-wide text-white/50">{meta.label}</p>
        <p className="text-base font-semibold text-white mt-0.5 truncate">{teamName(team)}</p>
      </div>
    </motion.div>
  );
}
